import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Alert, Badge, Button, Card, EmptyState } from '../components/ui.jsx';
import api from '../utils/api';
import { STAGE_LABELS } from '../utils/format';
import {
  buildMonthMatrix,
  formatCalendarDayKey,
  getCalendarRange,
  getEventApplicantUrl,
  getEventsForDay,
  groupEventsByDay,
} from '../utils/calendarEvents.js';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS_LABELS = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  no_show: 'No show',
  cancelled: 'Cancelled',
};
const STATUS_VARIANTS = {
  scheduled: 'info',
  completed: 'success',
  no_show: 'warning',
  cancelled: 'neutral',
};

function cx(...classes) {
  return classes.flat().filter(Boolean).join(' ');
}

function getApplicantDisplayName(applicant) {
  return [applicant?.first_name, applicant?.last_name].filter(Boolean).join(' ').trim() || 'Applicant';
}

function formatMonthLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatDayHeading(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
}

function formatEventTime(start) {
  return new Date(start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function toDateTimeLocalValue(date = new Date()) {
  const rounded = new Date(date);
  rounded.setMinutes(Math.ceil(rounded.getMinutes() / 15) * 15, 0, 0);
  const offsetMs = rounded.getTimezoneOffset() * 60 * 1000;
  return new Date(rounded.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value) {
  return value ? new Date(value).toISOString() : '';
}

export default function Calendar() {
  const navigate = useNavigate();
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [applicantSearch, setApplicantSearch] = useState('');
  const [applicantOptions, setApplicantOptions] = useState([]);
  const [applicantSearchLoading, setApplicantSearchLoading] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState('');
  const [appointmentAt, setAppointmentAt] = useState(() => toDateTimeLocalValue());
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const weeks = useMemo(() => buildMonthMatrix(visibleMonth), [visibleMonth]);
  const groupedEvents = useMemo(() => groupEventsByDay(events), [events]);
  const selectedDayEvents = useMemo(
    () => getEventsForDay(groupedEvents, selectedDate),
    [groupedEvents, selectedDate]
  );
  const selectedDayKey = formatCalendarDayKey(selectedDate);

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const range = getCalendarRange(visibleMonth);
      const params = new URLSearchParams(range);
      const data = await api.get(`/calendar/events?${params}`);
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message || 'Calendar events could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [visibleMonth]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (!showScheduleModal) return undefined;
    const searchTerm = applicantSearch.trim();
    const timeoutId = window.setTimeout(async () => {
      setApplicantSearchLoading(true);
      try {
        const params = new URLSearchParams({ limit: '10', sort: 'newest' });
        if (searchTerm) params.set('search', searchTerm);
        const data = await api.get(`/applicants?${params}`);
        setApplicantOptions(data.applicants || []);
      } catch (err) {
        setApplicantOptions([]);
        setScheduleError(err.message || 'Applicant search failed.');
      } finally {
        setApplicantSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [applicantSearch, showScheduleModal]);

  const openScheduleModal = () => {
    setScheduleError('');
    setAppointmentAt(toDateTimeLocalValue(selectedDate));
    setAppointmentNotes('');
    setSelectedApplicantId('');
    setApplicantSearch('');
    setShowScheduleModal(true);
  };

  const closeScheduleModal = () => {
    if (saving) return;
    setShowScheduleModal(false);
    setScheduleError('');
  };

  const moveMonth = (direction) => {
    const nextMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + direction, 1);
    setVisibleMonth(nextMonth);
    setSelectedDate(nextMonth);
  };

  const goToToday = () => {
    const today = new Date();
    setVisibleMonth(today);
    setSelectedDate(today);
  };

  const handleEventClick = (event) => {
    const url = getEventApplicantUrl(event);
    if (url) navigate(url);
  };

  const handleScheduleAppointment = async (event) => {
    event.preventDefault();
    if (!selectedApplicantId || !appointmentAt || saving) return;
    setSaving(true);
    setScheduleError('');
    try {
      const response = await api.post('/calendar/events', {
        applicant_id: selectedApplicantId,
        scheduled_at: fromDateTimeLocalValue(appointmentAt),
        notes: appointmentNotes.trim(),
      });
      setShowScheduleModal(false);
      setAppointmentNotes('');
      setSelectedApplicantId('');
      await fetchEvents();
      showToast('Appointment scheduled');
      if (response?.event?.applicantUrl) navigate(response.event.applicantUrl);
    } catch (err) {
      setScheduleError(err.message || 'Appointment could not be scheduled.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-5 lg:px-6">
        {toast && (
          <div className="fixed right-4 top-4 z-50 rounded-xl bg-success-text px-4 py-3 text-sm font-semibold text-white shadow-lg" role="status">
            {toast}
          </div>
        )}

        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-text-muted">Interview calendar</p>
            <h2 className="text-2xl font-bold tracking-[-0.02em] text-text-primary">{formatMonthLabel(visibleMonth)}</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => moveMonth(-1)}>Previous</Button>
            <Button type="button" variant="secondary" onClick={goToToday}>Today</Button>
            <Button type="button" variant="secondary" onClick={() => moveMonth(1)}>Next</Button>
            <Button type="button" variant="primary" onClick={openScheduleModal}>Schedule appointment</Button>
          </div>
        </div>

        {error && <Alert variant="danger" title="Calendar unavailable" className="mb-4">{error}</Alert>}

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
          <Card padding="compact" className="overflow-hidden">
            <div className="grid grid-cols-7 border-b border-border-default bg-surface-subtle text-center text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
              {WEEKDAY_LABELS.map(day => <div key={day} className="px-2 py-3">{day}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {weeks.flat().map(day => {
                const dayEvents = getEventsForDay(groupedEvents, day.key);
                const isSelected = day.key === selectedDayKey;
                return (
                  <button
                    type="button"
                    key={day.key}
                    onClick={() => setSelectedDate(day.date)}
                    className={cx(
                      'min-h-28 border-b border-r border-border-default bg-white p-2 text-left transition-colors focus-ring sm:min-h-36',
                      !day.isCurrentMonth && 'bg-surface-subtle text-text-muted',
                      isSelected && 'bg-brand-blueSoft ring-2 ring-inset ring-brand-blue',
                    )}
                    aria-pressed={isSelected}
                  >
                    <span className={cx(
                      'inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold',
                      day.isToday ? 'bg-brand-blue text-white' : 'text-text-primary'
                    )}>
                      {day.label}
                    </span>
                    <div className="mt-2 space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <span key={event.id} className="block truncate rounded-lg bg-info-bg px-2 py-1 text-[11px] font-semibold text-info-text">
                          {formatEventTime(event.start)} {event.title}
                        </span>
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="block text-[11px] font-semibold text-text-muted">+{dayEvents.length - 3} more</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="self-start">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-text-primary">{formatDayHeading(selectedDate)}</h3>
                <p className="text-sm text-text-muted">{selectedDayEvents.length} scheduled appointment{selectedDayEvents.length === 1 ? '' : 's'}</p>
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={openScheduleModal}>Add</Button>
            </div>

            {loading ? (
              <div className="py-10 text-center text-sm text-text-muted">Loading calendar events...</div>
            ) : selectedDayEvents.length === 0 ? (
              <EmptyState
                icon="📅"
                title="No interviews scheduled"
                description="Pick another day or schedule an appointment for an existing applicant."
                action={<Button type="button" size="sm" onClick={openScheduleModal}>Schedule appointment</Button>}
              />
            ) : (
              <div className="space-y-3">
                {selectedDayEvents.map(event => (
                  <button
                    type="button"
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className="w-full rounded-card border border-border-default bg-white p-4 text-left shadow-sm transition-colors hover:bg-surface-hover focus-ring"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-text-primary">{event.title}</p>
                        <p className="text-sm text-text-muted">{formatEventTime(event.start)}</p>
                      </div>
                      <Badge variant={STATUS_VARIANTS[event.status] || 'neutral'}>{STATUS_LABELS[event.status] || event.status}</Badge>
                    </div>
                    {event.stage && <p className="mt-2 text-xs font-semibold text-text-muted">Stage: {STAGE_LABELS[event.stage] || event.stage}</p>}
                    <p className="mt-3 text-sm font-semibold text-brand-blue">Open applicant →</p>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {showScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="schedule-title">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <form onSubmit={handleScheduleAppointment} className="p-5 sm:p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <h2 id="schedule-title" className="text-xl font-bold text-text-primary">Schedule appointment</h2>
                  <p className="text-sm text-text-muted">Select an existing applicant and appointment time.</p>
                </div>
                <button type="button" onClick={closeScheduleModal} className="rounded-app px-3 py-2 text-sm font-semibold text-text-muted hover:bg-surface-hover focus-ring">
                  Close
                </button>
              </div>

              {scheduleError && <Alert variant="danger" className="mb-4">{scheduleError}</Alert>}

              <div className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-text-primary">Applicant search</span>
                  <input
                    type="search"
                    value={applicantSearch}
                    onChange={(event) => setApplicantSearch(event.target.value)}
                    placeholder="Search by applicant name"
                    className="w-full rounded-app border border-border-default bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-text-primary">Applicant</span>
                  <select
                    value={selectedApplicantId}
                    onChange={(event) => setSelectedApplicantId(event.target.value)}
                    required
                    className="w-full rounded-app border border-border-default bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                  >
                    <option value="">{applicantSearchLoading ? 'Searching applicants...' : 'Select applicant'}</option>
                    {applicantOptions.map(applicant => (
                      <option key={applicant.id} value={applicant.id}>{getApplicantDisplayName(applicant)}</option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-text-primary">Date and time</span>
                  <input
                    type="datetime-local"
                    value={appointmentAt}
                    onChange={(event) => setAppointmentAt(event.target.value)}
                    required
                    className="w-full rounded-app border border-border-default bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-text-primary">Notes</span>
                  <textarea
                    value={appointmentNotes}
                    onChange={(event) => setAppointmentNotes(event.target.value)}
                    rows={4}
                    placeholder="Optional internal appointment notes"
                    className="w-full rounded-app border border-border-default bg-white px-3 py-2 text-sm text-text-primary outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                  />
                </label>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="secondary" onClick={closeScheduleModal} disabled={saving}>Cancel</Button>
                <Button type="submit" variant="primary" loading={saving} disabled={!selectedApplicantId || !appointmentAt}>Schedule</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
